import React, { useState, useEffect, useRef } from 'react'
import "dropdown-menu-pattern/dist/main.css"

interface DropdownOption {
  id: string | number;
  value: string;
}

interface DropdownProps {
  label: string;
  selectedValue: string;
  options: (string | DropdownOption)[]; // Allow options to be strings or objects
  onSelect: (value: string) => void;
  className?: string;
  placeholder?: string;
  fieldSelect?: boolean;
  error?: string;
  fieldName?: string;
  hasError?: boolean;
  disabled?: boolean;
}

/**
 * Normalizes an option to extract id and value
 */
const normalizeOption = (
  option: string | DropdownOption,
  index: number
): { key: string | number; value: string } => {
  if (typeof option === 'object') {
    return {
      key: option.id || index,
      value: option.value,
    };
  }
  return {
    key: option,
    value: option,
  };
};

/**
 * Validates options array
 */
const getValidOptions = (options: (string | DropdownOption)[]): (string | DropdownOption)[] => {
  return Array.isArray(options) ? options : [];
};

/**
 * Determines error state from multiple sources
 */
const hasErrorState = (error?: string, hasError?: boolean): boolean => {
  return !!(error ?? hasError);
};

/**
 * Renders options for both select and custom dropdown
 */
interface OptionRendererProps {
  options: (string | DropdownOption)[];
  selectedValue: string;
  onSelect: (value: string) => void;
  renderAsSelect?: boolean;
}

const OptionRenderer: React.FC<OptionRendererProps> = ({
  options,
  selectedValue,
  onSelect,
  renderAsSelect = false,
}) => {
  const validOptions = getValidOptions(options);

  if (renderAsSelect) {
    return (
      <>
        {validOptions.map((option, index) => {
          const { key, value } = normalizeOption(option, index);
          return (
            <option key={key} value={value}>
              {value}
            </option>
          );
        })}
      </>
    );
  }

  return (
    <ul className="drop-menu-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
      {validOptions.map((option, index) => {
        const { key, value } = normalizeOption(option, index);
        const isSelected = selectedValue === value;

        return (
          <li key={key} style={{ padding: 0, margin: 0 }}>
            <button
              type="button"
              className={`drop-menu-list-op ${isSelected ? 'active' : ''}`}
              onClick={() => onSelect(value)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '0.75rem 0.5rem', justifyContent: 'flex-start', flexDirection: 'row', textAlign: 'left', margin: 0, border: 'none', cursor: 'pointer', color: 'inherit' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F2F8FB'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <span style={{ flex: '0 0 16px', order: 1, minWidth: 16 }}>
                {isSelected ? (
                  <img
                    className="drop-menu-button-img"
                    src="/assets/images/checkmarkIcon.svg"
                    alt="selected"
                    width={16}
                    height={16}
                  />
                ) : (
                  <span style={{ width: 16, height: 16, display: 'inline-block' }} />
                )}
              </span>

              <span className="drop-menu-list-text" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, order: 2, textAlign: 'left' }}>{value}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

/**
 * Native select field variant
 */
interface SelectFieldVariantProps {
  label: string;
  selectedValue: string;
  options: (string | DropdownOption)[];
  onSelect: (value: string) => void;
  className?: string;
  placeholder?: string;
  fieldName?: string;
  hasError: boolean;
  disabled?: boolean;
}

const SelectFieldVariant: React.FC<SelectFieldVariantProps> = ({
  label,
  selectedValue,
  options,
  onSelect,
  className = '',
  placeholder = 'Choose option',
  fieldName,
  hasError,
  disabled = false,
}) => {
  const errorClass = hasError ? 'field-error' : '';

  return (
    <div className={`w-full ${className}`}>
      <div
        className="ds-form w-full"
        data-ds-theme="vessel"
        data-ds-variant="select"
        data-ds-version="2"
        style={{ maxWidth: '100%' , width: '100%' }}
      >
        <label className="ds-dropdown__label" >
          <span className="drop-menu-label-text" >{label}</span>
        </label>

        <div
          className={`select-one w-full ${errorClass}`}
          style={{ maxWidth: '100%', width: '100%' }}
        >
          <select
            id={fieldName}
            name={fieldName}
            className={`w-full ${errorClass}`}
            style={{ maxWidth: '100%', width: '100%' }}
            value={selectedValue}
            onChange={(e) => onSelect(e.target.value)}
            aria-invalid={hasError ? 'true' : 'false'}
            disabled={disabled}
          >
            <option value="" disabled hidden>
              {placeholder}
            </option>

            <OptionRenderer
              options={options}
              selectedValue={selectedValue}
              onSelect={onSelect}
              renderAsSelect
            />
          </select>

          <img
            className="drop-down-menu-chevron ml-2"
            src="/assets/images/chevrondown.svg"
            alt="chevron down"
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Custom dropdown pattern variant
 */
interface CustomDropdownVariantProps {
  label: string;
  selectedValue: string;
  options: (string | DropdownOption)[];
  onSelect: (value: string) => void;
  className?: string;
  placeholder?: string;
}

const CustomDropdownVariant: React.FC<CustomDropdownVariantProps> = ({
  label,
  selectedValue,
  options,
  onSelect,
  className = '',
  placeholder = 'Choose option',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleSelect = (value: string) => {
    onSelect(value);
    setIsOpen(false);
  };

  return (
    <div
      ref={dropdownRef}
      className={`drop-menu-pattern --default-option --initialized ${className}`}
      data-menu-type="standard"
      data-ds-theme="vessel"
      data-ds-variant="basic"
      data-ds-version="3"
      data-select-label="selected"
      data-list-type="option"
    >
      <div className="drop-menu">
        <label className="drop-menu-label">
          <span className="drop-menu-label-text">{label}</span>
        </label>

        <button
          className="drop-menu-dropdown"
          onClick={toggleDropdown}
          type="button"
          aria-expanded={isOpen}
        >
          <span className="drop-menu-dropdown-text">
            {selectedValue || placeholder}
          </span>
          <img
            className="drop-down-menu-chevron"
            src="/assets/images/chevrondown.svg"
            alt="chevron down"
          />
        </button>

        <div style={{ display: isOpen ? 'block' : 'none' }}>
          <OptionRenderer
            options={options}
            selectedValue={selectedValue}
            onSelect={handleSelect}
            renderAsSelect={false}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Dropdown component with two rendering variants:
 * - fieldSelect: Native HTML select for form fields
 * - Custom: Custom dropdown pattern for UI consistency
 *
 * Supports both string and object options with flexible error handling.
 */
const Dropdown: React.FC<DropdownProps> = ({
  label,
  selectedValue,
  options,
  onSelect,
  className = '',
  placeholder = 'Choose option',
  fieldSelect = false,
  error,
  fieldName,
  hasError = false,
  disabled = false,
}) => {
  const errorState = hasErrorState(error, hasError);

  if (fieldSelect) {
    return (
      <SelectFieldVariant
        label={label}
        selectedValue={selectedValue}
        options={options}
        onSelect={onSelect}
        className={className}
        placeholder={placeholder}
        fieldName={fieldName}
        hasError={errorState}
        disabled={disabled}
      />
    );
  }

  return (
    <CustomDropdownVariant
      label={label}
      selectedValue={selectedValue}
      options={options}
      onSelect={onSelect}
      className={className}
      placeholder={placeholder}
    />
  );
};

export default Dropdown;